/**
 * ProgramAutocomplete Component
 * Autocomplete picker for assigning a contract to a capped program (MTN program).
 * Filters active programs by the contract's issuer and investment type, and shows
 * the remaining headroom (limit minus committed capital) for the selected program —
 * mirroring the rollover PriorContractAutocomplete "remaining after allocation" UX.
 *
 * The DB hard-blocks over-limit contracts (sp_contract_create / sp_contract_update);
 * this component's warning is advisory.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { apiGet, extractApiData } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import logger from '../../utils/logger';

const filterOptions = (options, { inputValue }) => {
  const search = inputValue.toLowerCase();
  if (!search) return options;
  return options.filter((opt) => (opt.name || '').toLowerCase().includes(search));
};

function ProgramAutocomplete({
  value,
  onChange,
  onProgramResolved,
  // Product-first: the parent fetches the program list for the chosen product
  // (+issuer) so it can decide whether to render this field at all, and passes
  // it in here. When `options` is provided the component does not fetch.
  options: providedOptions = null,
  issuerId = null,
  investmentTypeId = null,
  excludeContractId = null,
  capitalAmount = 0,
  // Contract's currency (e.g. 'MWK'). When the selected program is in a
  // different currency (e.g. 'SZL'), the component fetches the FX rate and
  // displays headroom in program currency with an annotation of the converted
  // value at the rate. The DB ultimately enforces the cap in program currency
  // (with a snapshot on save).
  contractCurrencyCode = null,
  disabled = false,
  error,
  helperText,
  label = 'Program',
}) {
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const usingProvided = Array.isArray(providedOptions);
  const options = usingProvided ? providedOptions : fetchedOptions;

  // Self-fetch fallback (only when the parent did not provide options): filter
  // by investment type and/or issuer. The selected program is product-first.
  useEffect(() => {
    if (usingProvided) return undefined;
    let cancelled = false;
    if (!investmentTypeId && !issuerId) {
      setFetchedOptions([]);
      return undefined;
    }
    const fetchPrograms = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (issuerId) params.set('issuer_id', issuerId);
        if (investmentTypeId) params.set('investment_type_id', investmentTypeId);
        if (excludeContractId) params.set('exclude_id', excludeContractId);
        const response = await apiGet(`/refdata/programs?${params.toString()}`);
        const data = extractApiData(response);
        if (!cancelled) setFetchedOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        logger.error('Error fetching programs', { message: err.message });
        if (!cancelled) setFetchedOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPrograms();
    return () => { cancelled = true; };
  }, [usingProvided, issuerId, investmentTypeId, excludeContractId]);

  const selectedOption = options.find((opt) => opt.id === value) || null;

  // Notify the parent of the resolved program object once options load (so the
  // contract form can constrain Product Type + show headroom on edit-mode mount).
  useEffect(() => {
    if (onProgramResolved && value && selectedOption) {
      onProgramResolved(selectedOption);
    }
  }, [selectedOption, value, onProgramResolved]);

  // Cross-currency FX rate (contract -> program). Null when same-ccy or no rate
  // configured. The DB enforces the cap; this rate is for display only and is
  // computed exactly the same way at save (so what the user sees matches what
  // gets snapshotted).
  const programCcy = selectedOption?.currency_code || null;
  const isCrossCurrency = !!programCcy && !!contractCurrencyCode && programCcy !== contractCurrencyCode;
  const [fxRate, setFxRate] = useState(null);
  const [fxMissing, setFxMissing] = useState(false);

  const fetchFxRate = useCallback(async (from, to) => {
    if (!from || !to || from === to) { setFxRate(null); setFxMissing(false); return; }
    try {
      // /exchange-rates/current/:pair returns { success, rate: <row> } where
      // row.rate is the numeric rate. Read the row directly — extractApiData
      // looks for .data which this endpoint doesn't use.
      const response = await apiGet(`/exchange-rates/current/${from}/${to}`);
      const rateValue = response?.rate?.rate;
      if (rateValue !== undefined && rateValue !== null) {
        const num = parseFloat(rateValue);
        if (Number.isFinite(num)) {
          setFxRate(num);
          setFxMissing(false);
          return;
        }
      }
      setFxRate(null);
      setFxMissing(true);
    } catch (err) {
      logger.error('Error fetching FX rate', { message: err.message });
      setFxRate(null);
      setFxMissing(true);
    }
  }, []);

  useEffect(() => {
    if (isCrossCurrency) {
      fetchFxRate(contractCurrencyCode, programCcy);
    } else {
      setFxRate(null);
      setFxMissing(false);
    }
  }, [isCrossCurrency, contractCurrencyCode, programCcy, fetchFxRate]);

  // Amounts here are always real computed numbers; keep the '0.00' fallback for the
  // NaN case (formatNumber returns '-') so limit/headroom displays never read blank.
  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return formatNumber(num, 2);
  };

  const handleChange = (_, newValue) => {
    onChange(newValue?.id || null, newValue);
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      loading={loading}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      renderOption={(props, option) => {
        const hasLimit = option.limit_amount !== null && option.limit_amount !== undefined;
        const available = parseFloat(option.available);
        const isOver = hasLimit && available < 0;
        const expiryStr = option.expiry_date ? new Date(option.expiry_date).toLocaleDateString('en-GB') : null;
        return (
          <li {...props} key={option.id}>
            <Box sx={{ width: '100%', py: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {option.is_diminishing && (
                    <Typography variant="caption" color="warning.main" fontWeight="bold">Diminishing</Typography>
                  )}
                  {option.listed && (
                    <Typography variant="caption" color="primary">Listed</Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                <Typography variant="caption" color="textSecondary">
                  Used: {option.currency_code} {formatAmount(option.used_capital)}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color={!hasLimit ? 'textSecondary' : (isOver ? 'error' : 'success.main')}
                >
                  {hasLimit ? `Remaining: ${option.currency_code} ${formatAmount(available)}` : 'No limit'}
                </Typography>
              </Box>
              {expiryStr && (
                <Box sx={{ mt: 0.25 }}>
                  <Typography variant="caption" color="textSecondary">Expires {expiryStr}</Typography>
                </Box>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => {
        let computedHelperText = helperText || 'Select the capped program for this product';
        let wouldExceed = false;
        let crossCcyNoRate = false;

        if (selectedOption) {
          const hasLimit = selectedOption.limit_amount !== null && selectedOption.limit_amount !== undefined;
          // Cross-currency: convert contract capital into program currency for
          // the cap maths (the DB does exactly this at save).
          const amount = parseFloat(capitalAmount) || 0;
          const amountInProgramCcy = isCrossCurrency
            ? (fxRate ? amount * fxRate : null)
            : amount;
          if (isCrossCurrency && fxMissing) {
            crossCcyNoRate = true;
          }

          if (!hasLimit) {
            computedHelperText = `${selectedOption.currency_code} — no limit`;
            if (isCrossCurrency && fxRate) {
              computedHelperText += ` (this contract ≈ ${selectedOption.currency_code} ${formatAmount(amountInProgramCcy)} at ${contractCurrencyCode}/${selectedOption.currency_code} ${fxRate.toFixed(4)})`;
            } else if (crossCcyNoRate) {
              computedHelperText = `No FX rate configured for ${contractCurrencyCode} → ${selectedOption.currency_code} — add one in Reference Data > FX Rates before saving`;
            }
          } else if (crossCcyNoRate) {
            computedHelperText = `No FX rate configured for ${contractCurrencyCode} → ${selectedOption.currency_code} — add one in Reference Data > FX Rates before saving`;
          } else {
            const available = parseFloat(selectedOption.available) || 0;
            const afterAllocation = amountInProgramCcy !== null ? available - amountInProgramCcy : available;
            wouldExceed = amountInProgramCcy !== null && afterAllocation < 0;
            computedHelperText = `Remaining after this contract: ${selectedOption.currency_code} ${formatAmount(afterAllocation)} of ${formatAmount(selectedOption.limit_amount)}`;
            if (isCrossCurrency && fxRate) {
              computedHelperText += ` (≈ ${contractCurrencyCode} ${formatAmount(amount)} at ${contractCurrencyCode}/${selectedOption.currency_code} ${fxRate.toFixed(4)})`;
            }
          }
          // Annotate mode + expiry so the user sees the rules they're committing to.
          const extras = [];
          if (selectedOption.is_diminishing) extras.push('Diminishing');
          if (selectedOption.expiry_date) extras.push(`expires ${new Date(selectedOption.expiry_date).toLocaleDateString('en-GB')}`);
          if (extras.length) computedHelperText = `${computedHelperText} · ${extras.join(' · ')}`;
        }

        const amountInProgCcyForWarning = isCrossCurrency
          ? (fxRate ? (parseFloat(capitalAmount) || 0) * fxRate : null)
          : (parseFloat(capitalAmount) || 0);
        const overBy = amountInProgCcyForWarning !== null
          ? Math.abs((parseFloat(selectedOption?.available) || 0) - amountInProgCcyForWarning)
          : 0;
        return (
          <TextField
            {...params}
            label={label}
            error={!!error || wouldExceed || crossCcyNoRate}
            helperText={
              error?.message
                ? error.message
                : wouldExceed
                  ? `Warning: this contract exceeds the program limit by ${selectedOption.currency_code} ${formatAmount(overBy)}`
                  : computedHelperText
            }
            InputLabelProps={{ shrink: true }}
          />
        );
      }}
    />
  );
}

export default ProgramAutocomplete;
